import React from 'react';
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  VStack,
  HStack,
  Checkbox,
  CheckboxGroup,
  useToast,
  SimpleGrid,
  Text,
  Divider,
  Spinner,
  Image,
  IconButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import dayjs from 'dayjs';
import DatePicker from 'components/datePicker/DatePicker';
import xhr from 'xhr';

function AdminCreateEvent() {
  const toast = useToast();
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [zoomLink, setZoomLink] = React.useState('');
  const [inviteAt, setInviteAt] = React.useState(null);
  const [startDate, setStartDate] = React.useState(null);
  const [startTime, setStartTime] = React.useState('');
  const [duration, setDuration] = React.useState('60');
  const [selectedTypes, setSelectedTypes] = React.useState([]);
  const [imageFile, setImageFile] = React.useState(null);
  const [imagePreview, setImagePreview] = React.useState('');
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const [programTypes, setProgramTypes] = React.useState([]);
  const [loadingTypes, setLoadingTypes] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [recipientsCount, setRecipientsCount] = React.useState(0);
  const [eventsList, setEventsList] = React.useState([]);
  const [loadingCounts, setLoadingCounts] = React.useState(false);
  const [counts, setCounts] = React.useState({});

  React.useEffect(() => {
    const getProgramTypes = async () => {
      try {
        setLoadingTypes(true);
        const res = await xhr(`/program-types?fields[0]=name&fields[1]=slug&pagination[limit]=-1`);
        const items = res?.data?.data ?? [];
        items.sort((a, b) =>
          (a?.attributes?.name || '').toLowerCase().localeCompare((b?.attributes?.name || '').toLowerCase(), 'es')
        );
        setProgramTypes(items);
      } catch {
        toast({ title: 'No se pudieron cargar los tipos de programa', status: 'error' });
      } finally {
        setLoadingTypes(false);
      }
    };
    getProgramTypes();
  }, [toast]);



  const isValidZoom = (url) => /^https?:\/\/([a-z0-9.-]+\.)?zoom\.us\/.+/i.test((url || '').trim());

  const validateForm = () => {
    if (!title.trim()) return toast({ title: 'Falta el nombre del evento', status: 'warning' }), false;
    if (!zoomLink.trim() || !isValidZoom(zoomLink)) return toast({ title: 'Link de Zoom inválido', status: 'warning' }), false;
    if (!inviteAt) return toast({ title: 'Falta la fecha de invitación (invite_at)', status: 'warning' }), false;
    if (!startDate) return toast({ title: 'Falta la fecha de inicio (start_at)', status: 'warning' }), false;
    if (!startTime) return toast({ title: 'Falta la hora de inicio (HH:mm)', status: 'warning' }), false;
    if (!duration || isNaN(Number(duration)) || Number(duration) <= 0) return toast({ title: 'Duración inválida (minutos)', status: 'warning' }), false;
    if (!selectedTypes.length) return toast({ title: 'Selecciona al menos un tipo de programa', status: 'warning' }), false;
    return true;
  };

  const handlePickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast({ title: 'Formato no permitido. Usa PNG, JPG o WEBP.', status: 'warning' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'La imagen supera 5MB.', status: 'warning' });
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const clearImage = () => {
    setImageFile(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview('');
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      setSubmitting(true);
      const [hh, mm] = (startTime || '').split(':');
      const startAtISO = dayjs(startDate).hour(Number(hh || 0)).minute(Number(mm || 0)).second(0).millisecond(0).toISOString();
      const createRes = await xhr.post('/events', {
        data: {
          title: title.trim(),
          description: description?.trim() || '',
          zoom_link: zoomLink.trim(),
          invite_at: dayjs(inviteAt).toISOString(),
          start_at: startAtISO,
          duration: Number(duration),
          program_types: selectedTypes.map((id) => Number(id)),
        },
      });
      const eventId = createRes?.data?.data?.id;
      if (!eventId) throw new Error('No se obtuvo el ID del evento creado.');
      if (imageFile) {
        setUploadingImage(true);
        const formData = new FormData();
        formData.append('files', imageFile, imageFile.name);
        formData.append('ref', 'api::event.event');
        formData.append('refId', eventId);
        formData.append('field', 'image');
        await xhr('/upload', { method: 'post', data: formData });
        setUploadingImage(false);
      }
      toast({ title: 'Evento creado correctamente', status: 'success' });
      setTitle(''); setDescription(''); setZoomLink('');
      setInviteAt(null); setStartDate(null); setStartTime(''); setDuration('60');
      setSelectedTypes([]); setRecipientsCount(0); clearImage();
    } catch (error) {
      const msg =
        error?.response?.data?.error?.details?.errors?.[0]?.message ||
        error?.response?.data?.error?.message ||
        error?.message ||
        'Error al crear el evento';
      toast({ title: msg, status: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const loadEventsCounts = async () => {
    try {
      setLoadingCounts(true);
      // Elimina los campos que no son de relación en la URL
      const evRes = await xhr(`/events?sort[0]=start_at:desc&pagination[limit]=-1`);
      console.log('Respuesta completa de la API:', evRes);
      const list = evRes?.data?.data ?? [];
      const results = await Promise.all(
        list.map(async (ev) => {
          console.log('Evento individual antes de procesar:', ev);
          const id = ev.id;
          const r = await xhr(`/assist-events?filters[event][id][$eq]=${id}&pagination[pageSize]=1&pagination[withCount]=true`);
          const total = r?.data?.meta?.pagination?.total ?? 0;
          return { id, title: ev?.title, start_at: ev?.start_at, total };
        })
      );
      setEventsList(results);
      const map = {};
      results.forEach((it) => (map[it.id] = it.total));
      setCounts(map);
    } catch {
      toast({ title: 'No se pudieron cargar los conteos', status: 'error' });
    } finally {
      setLoadingCounts(false);
    }
  };

  React.useEffect(() => {
    loadEventsCounts();
  }, []);

  return (
    <Box bg="white" p={6} borderRadius="xl" border="1px solid" borderColor="gray.200">
      <Heading size="md" mb={4}>Crear evento (clase en vivo)</Heading>
      <VStack spacing={4} align="stretch">
        <FormControl isRequired>
          <FormLabel>Nombre del evento</FormLabel>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej: Club de lectura bachillerato" />
        </FormControl>
        <FormControl>
          <FormLabel>Descripción</FormLabel>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Detalles para los estudiantes (opcional)" rows={4} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Link de Zoom</FormLabel>
          <Input value={zoomLink} onChange={(e) => setZoomLink(e.target.value)} placeholder="https://zoom.us/j/XXXXXXXXXXX" />
        </FormControl>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormControl isRequired>
            <FormLabel>Visible desde (invite_at)</FormLabel>
            <DatePicker date={inviteAt} onChange={setInviteAt} placeholder="Selecciona fecha y hora" />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Fecha y hora de inicio (start_at)</FormLabel>
            <DatePicker date={startDate} onChange={setStartDate} placeholder="Selecciona la fecha" />
            <HStack mt={2}>
              <FormLabel m={0} minW="120px">Hora (HH:mm)</FormLabel>
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </HStack>
          </FormControl>
        </SimpleGrid>
        <FormControl isRequired>
          <FormLabel>Duración (minutos)</FormLabel>
          <Input type="number" min={1} value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="60" />
        </FormControl>
        <FormControl>
          <FormLabel>Imagen del evento</FormLabel>
          <HStack align="start" spacing={4}>
            <Box>
              <Input type="file" accept="image/png,image/jpeg,image/webp" onChange={handlePickImage} />
              <Text fontSize="xs" color="gray.500" mt={1}>Formatos: PNG, JPG, WEBP. Máx. 5MB.</Text>
            </Box>
            {imagePreview && (
              <Box position="relative">
                <Image src={imagePreview} alt="Previsualización" boxSize="140px" objectFit="cover" borderRadius="md" border="1px solid #E2E8F0" />
                <IconButton aria-label="Quitar imagen" icon={<DeleteIcon />} size="sm" colorScheme="red" onClick={clearImage} position="absolute" top="2" right="2" />
              </Box>
            )}
          </HStack>
          {uploadingImage && (
            <HStack mt={2}>
              <Spinner size="sm" />
              <Text>Subiendo imagen…</Text>
            </HStack>
          )}
        </FormControl>
        <Divider />
        <FormControl isRequired>
          <FormLabel>Tipos de programa (puedes seleccionar varios)</FormLabel>
          {loadingTypes ? (
            <HStack><Spinner size="sm" /><Text>Cargando tipos de programa…</Text></HStack>
          ) : (
            <CheckboxGroup value={selectedTypes} onChange={(vals) => setSelectedTypes(vals)}>
              <VStack align="start" spacing={2} maxH="220px" overflowY="auto" border="1px solid #E2E8F0" borderRadius="md" p={3}>
                {programTypes.length === 0 ? (
                  <Text color="gray.500">No hay tipos de programa disponibles</Text>
                ) : (
                  programTypes.map((pt) => {
                    const label = pt?.attributes?.name ?? pt?.attributes?.slug ?? `${pt.name}`;
                    return <Checkbox key={pt.id} value={String(pt.id)}>{label}</Checkbox>;
                  })
                )}
              </VStack>
            </CheckboxGroup>
          )}
        </FormControl>
        <HStack justify="flex-end" pt={2}>
          <Button colorScheme="messenger" onClick={handleSubmit} isLoading={submitting} isDisabled={submitting || uploadingImage}>Crear evento</Button>
        </HStack>
      </VStack>
      <Divider my={8} />
      <Heading size="md" mb={3}>Participación por evento</Heading>
      <HStack mb={3}>
        <Button size="sm" onClick={loadEventsCounts} isLoading={loadingCounts}>Refrescar</Button>
      </HStack>
      <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflowX="auto">
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>#</Th>
              <Th>Evento</Th>
              <Th>Fecha</Th>
              <Th isNumeric>Inscritos</Th>
            </Tr>
          </Thead>
          <Tbody>
            {eventsList.length === 0 ? (
              <Tr><Td colSpan={4}><Text p={3} color="gray.500">Sin datos</Text></Td></Tr>
            ) : (
              eventsList.map((row, idx) => (
                <Tr key={row.id}>
                  <Td>{idx + 1}</Td>
                  <Td>{row.title}</Td>
                  <Td>{row.start_at ? dayjs(row.start_at).format('DD/MM/YYYY HH:mm') : '—'}</Td>
                  <Td isNumeric>{counts[row.id] ?? row.total ?? 0}</Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

export default AdminCreateEvent;
